'use client';

import { type FC, useMemo, useRef } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useNavigation } from '../../contexts/NavigationContext';
import { useRainHistory } from '../../queries/useRainQueries';
import { ScreenContent, ScreenFixedTop } from '../../screen';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Spinner } from '../../components/ui/Spinner';
import { RainHistoryList, RainHistorySkeleton } from '../../components/Rain';
import translations from './RainHistoryScreen.translations';

export const RainHistoryScreen: FC = () => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const query = useRainHistory();
  const rounds = useMemo(() => query.data?.pages.flatMap((p) => p.rains) ?? [], [query.data]);

  useInfiniteScroll({
    hasMore: query.hasNextPage,
    loading: query.isFetchingNextPage,
    onLoadMore: () => {
      void query.fetchNextPage();
    },
    threshold: 200,
    scrollRef,
  });

  let body;
  if (query.isLoading) {
    body = <RainHistorySkeleton />;
  } else if (query.error) {
    const message = t('errorLoading');
    body = (
      <ErrorMessage
        error={message}
        message={message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  } else {
    body = (
      <>
        <RainHistoryList
          rounds={rounds}
          onOpenUser={(userId) => navigation.navigate('profile', { userId })}
          onOpenRound={(rainId) => navigation.navigate('rain-details', { rainId })}
        />
        {query.isFetchingNextPage && (
          <div className="flex items-center justify-center p-4">
            <Spinner size="s" />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenContent className="flex-1" scrollRef={scrollRef}>
        {body}
      </ScreenContent>
    </>
  );
};
